import { action, makeObservable, observable, reaction } from 'mobx';
import { BaseController } from './core/base';
import { UsersRepository } from 'common/repositories/users';
import { UserProfile } from 'common/types/models/user';
import { Getter } from '@zajno/common/types';
import { IAuthController, IUserController } from './abstractions';
import { PremiumLevel } from 'common/types/models';
import { PromiseCacheObservable } from '@zajno/common-mobx/structures/promiseCache';
import { updateUserInSentry } from 'app/services/sentry';
import { FlagModel } from '@zajno/common-mobx/viewModels';

type AuthUser = IAuthController['user'];

const _OBSERVER_CURRENT_USER = 'observer:current-user';

export class UsersController extends BaseController implements IUserController {

    private _repository: UsersRepository = null;

    private _user: UserProfile = null;

    private _accountMissing: boolean = null;
    private _observingEnabled = false;

    private readonly _isInitializing = new FlagModel(false);

    constructor(readonly _auth: Getter<IAuthController>) {
        super();

        makeObservable<UsersController, '_user' | '_processUser' | '_accountMissing'>(this, {
            _user: observable.ref,
            _processUser: action,
            _accountMissing: observable,
        });
    }

    public get user() { return this._user; }
    public get repo() { return this._repository; }
    public get isInitializing() { return this._isInitializing.value; }

    public get accountMissing() { return this._accountMissing; }

    private get authUser() { return Getter.getValue(this._auth)?.user; }

    public setObservingEnabled(enabled: boolean) {
        if (this._observingEnabled === enabled) {
            return;
        }

        this._observingEnabled = enabled;
        if (enabled) {
            this._enableObserving();
        } else {
            this._disableObserving();
        }

        return this;
    }

    protected async onInitialize() {
        this._repository = new UsersRepositoryObservable(this.supabase)
            .useCaching(true);

        const getAuthUser = () => this.authUser;

        // sync DB user w/ authUser
        this.disposer.add(
            reaction(
                () => getAuthUser()?.id,
                () => this._processAuthUser(getAuthUser()),
                { fireImmediately: true },
            ),
        );
    }

    private _processAuthUser = (authUser: AuthUser) => {
        this._isInitializing.setTrue();

        this._disableObserving();

        if (!authUser) {
            this._processUser(null);
            this._isInitializing.setFalse();
            return;
        }

        this.runAction(async () => {
            const user = await this._repository.getUser(authUser.id);
            this._processUser(user);

            this._isInitializing.setFalse();
            this._enableObserving();
        }, 'syncUser');
    };

    private _processUser = (user: UserProfile | null) => {
        this._user = user;
        const authUser = this.authUser;
        this._accountMissing = !!authUser?.id && !user?.id;
        this.logger.log('processing current user:', authUser?.id, UserProfile.getEmail(user), user?.permissions);
        if (this._accountMissing) {
            this.logger.warn('User account is missing in DB for id =', authUser?.id, '; please try to login again.');
        }

        // TODO add inversion of control
        updateUserInSentry(user);
    };

    public setPremiumLevel(userId: string, premiumLevel: PremiumLevel) {
        return this.runAction(async () => {
            await this._repository.setPremiumLevel(userId, premiumLevel);
        }, 'setPremiumLevel');
    }

    public getUsers(offset: number, limit: number) {
        return this.runAction(async () => {
            const data = await this._repository.getUsers({
                pages: { offset, limit },
            });

            if (!data) {
                throw new Error(`Failed to get users: offset: ${offset}, limit: ${limit}`);
            }

            return data;
        }, 'getUsers with pagination');
    }

    public updateUserPersonalName(userId: string, name: string) {
        return this.runAction(async () => {
            const data = await this._repository.updateUserName(
                userId,
                name,
            );

            if (!data) {
                throw new Error(`Failed to update user personal info: name:${name} `);
            }

            return data;
        }, 'updateUserPersonalInfo name');
    }

    private _disableObserving() {
        this.disposer.execute(_OBSERVER_CURRENT_USER);
    }

    private _enableObserving() {
        if (this._observingEnabled && this._user?.id) {
            this.logger.log('enabling user observing for id =', this._user.id);
            this.disposer.add(
                this._repository.subscribeToUser(this._user.id, user => {
                    this._processUser(user);
                }),
                _OBSERVER_CURRENT_USER,
            );
        }
    }
}

class UsersRepositoryObservable extends UsersRepository {
    get PromiseCacheClass() { return PromiseCacheObservable; }
}

export default UsersController;
